import DBUtil from './DBUtil.jsx';
import Utils from '../util/Utils.jsx';
import { openAlert } from '../context/alert.js';
import { parseLayoutFilter, withValidLayoutGrouping } from '../context/projectLayout.js';

const db = new DBUtil(() => true/*dbResult => {
	if (dbResult.error) {
		this.alert('There was an error creating the offline database. You will not be able to use offline mode. Error reported: ' + dbResult.error.message);
	} else {
		updateOfflineChangesState();
	}
}*/);

/** @param {any} dbResult */
const getRowsLength = (dbResult) => {
	const length = dbResult?.rs?.rows?.length;
	return typeof length === 'number' ? length : 0;
};

export const storeOfflineDataset = (dispatch, datasetId) => {
	Utils.ajax('getDatasets', { specificDataset: datasetId }, dispatch, result => {
		if (!result?.success || !result.datasets?.[datasetId]) {
			return;
		}
		db.query('DELETE FROM offlineDatasets WHERE Id = ?', [datasetId], () => {
			db.query('INSERT INTO offlineDatasets (Id, JSON) VALUES (?, ?)', [datasetId, JSON.stringify(result.datasets[datasetId])], () => {
				Utils.ajax('getDatasetInfo', { dataset: datasetId }, dispatch, infoResult => {
					db.query('DELETE FROM offlineDatasetsInfo WHERE Id = ?', [datasetId], () => {
						db.query('INSERT INTO offlineDatasetsInfo (Id, JSON) VALUES (?, ?)', [datasetId, JSON.stringify(infoResult)]);
						dispatch(openAlert('Downloaded', 'This project was downloaded to your device and changes will be synced as an internet connection allows.'));
					});
				});
			});
		});
	});
};

export const getOfflineDatasets = dispatch => {
	return new Promise((resolve) => {
		db.query('SELECT * FROM offlineDatasets', [], dbResult => {
			try {
				if (dbResult?.error) {
					dispatch(openAlert('Error', 'There was an error trying to read offline data (Code: 001). Please try again when you\'re online. Error reported: ' + dbResult.error.message));
					resolve({
						success: false,
						datasets: {}
					});
					return;
				}
				const rowCount = getRowsLength(dbResult);
				if (!rowCount) {
					dispatch(openAlert('No offline projects', 'You are offline but there are no offline projects stored. Please try again when you\'re online.'));
					resolve({
						success: true,
						datasets: {}
					});
					return;
				}

				const offlineDatasets = {};
				for (let i = 0; i < rowCount; i++) {
					const row = dbResult.rs.rows.item(i);
					if (!row) continue;
					const json = Utils.safeJSONParse(row.JSON);

					if (!json) {
						removeOfflineDataset(row.Id);
					} else {
						offlineDatasets[row.Id] = json;
					}
				}

				resolve({
					success: true,
					datasets: offlineDatasets
				});
			} catch (error) {
				console.error('getOfflineDatasets failed:', error);
				resolve({ success: false, datasets: {} });
			}
		});
	});
};

export const removeOfflineDataset = datasetId => {
	db.query('DELETE FROM offlineDatasets WHERE Id = ?', [datasetId]);
	db.query('DELETE FROM offlineDatasetsInfo WHERE Id = ?', [datasetId]);
};

export const getOfflineDatasetInfo = (dispatch, datasetId, layoutId) => {
	return new Promise((resolve, reject) => {
		db.query('SELECT * FROM offlineDatasetsInfo WHERE Id = ?', [datasetId], dbResult => {
			try {
				if (dbResult?.error) {
					dispatch(openAlert('There was an error trying to read offline data (Code: 004). Please try again when you\'re online. Error reported: ' + dbResult.error.message));
					reject({ success: false });
					return;
				}
				if (!getRowsLength(dbResult)) {
					dispatch(openAlert('You are offline and this project has not been downloaded to your device. Go online and select "Allow Offline" in setup to download this project (Code: 005).'));
					reject({ success: false });
					return;
				}

				const row = dbResult.rs.rows.item(0);
				const json = Utils.safeJSONParse(row?.JSON);

				if (!json) {
					dispatch(openAlert('There was an error trying to read offline data (Code: 005). Please try again when you\'re online.'));
					if (row?.Id != null) removeOfflineDataset(row.Id);
					reject({ success: false });
					return;
				}

				const layouts = Array.isArray(json.layouts) ? json.layouts : [];
				let lastLayout = false;
				for (const layout of layouts) {
					layout.Filter = parseLayoutFilter(layout.Filter);
					if (layout.Id === layoutId || (!layoutId && layout.LastUsed === '1')) lastLayout = layout;
				}
				if (lastLayout) {
					lastLayout = withValidLayoutGrouping(lastLayout, json.activityCodesTree);
				}

				resolve({
					success: true,
					...json,
					layouts,
					...(lastLayout || { ViewType: 'WBS List', GroupBy: null, GroupByActivityCode: null })
				});
			} catch (error) {
				console.error('getOfflineDatasetInfo failed:', error);
				reject({ success: false });
			}
		});
	});
};

export const getOfflineChanges = () => {
	return new Promise((resolve) => {
		db.query('SELECT rowid, * FROM offlineChanges', dbResult => {
			try {
				if (dbResult?.error) {
					resolve({ success: false, offlineChanges: [], error: dbResult.error });
					return;
				}

				const offlineChanges = [];
				const rowCount = getRowsLength(dbResult);
				for (let i = 0; i < rowCount; i++) {
					const row = dbResult.rs.rows.item(i);
					if (!row) continue;
					const json = Utils.safeJSONParse(row.JSON);
					offlineChanges.push({ datasetId: row.DatasetId, data: json === false ? 'Error' : json });
				}

				resolve({ success: true, offlineChanges });
			} catch (error) {
				console.error('getOfflineChanges failed:', error);
				resolve({ success: false, offlineChanges: [], error });
			}
		});
	});
};

export const clearOfflineChanges = (callback) => {
	db.query('DELETE FROM offlineChanges', [], () => {
		if (typeof callback === 'function') {
			callback();
		}
	});
};

export const uploadOfflineChange = (dispatch, callback) => {
	const finish = () => {
		if (typeof callback === 'function') {
			callback();
		}
	};

	if (!Utils.isOnline() || Utils.authBlocked || !localStorage.getItem('token')) {
		finish();
		return;
	}

	db.query('SELECT rowid, * FROM offlineChanges', dbResult => {
		try {
			if (dbResult?.error || !dbResult?.rs?.rows) {
				finish();
				return;
			}

			const changesLength = getRowsLength(dbResult);
			if (changesLength) {
				if (Utils.authBlocked || !localStorage.getItem('token')) {
					finish();
					return;
				}

				const row = dbResult.rs.rows.item(0);
				const json = Utils.safeJSONParse(row?.JSON);

				// If we can't parse the data, we'll never be able to upload it. Delete it and move on.
				if (!json) {
					deleteOfflineChange(dispatch, row.rowid, changesLength, callback);
					return;
				}

				json.dataset = row.DatasetId;
				const uploadChange = endpoint => {
					Utils.ajaxAsync(endpoint, json, dispatch, { suppressErrorAlert: true }).then(result => {
						if (result && result.reauthenticate) {
							finish();
							return;
						}
						if (result && result.success) {
							deleteOfflineChange(dispatch, row.rowid, changesLength, callback);
							return;
						}
						finish();
					}).catch(() => {
						finish();
					});
				};
				if (row.Type === 'setProgress') {
					uploadChange('setProgress');
				} else if (row.Type === 'setNote') {
					if (json.noteId && json.noteId.indexOf('temp-') !== -1) {
						json.tempId = json.noteId;
						json.noteId = '';
					}
					uploadChange('setNote');
				} else {
					finish();
				}
			} else {
				finish();
			}
		} catch (error) {
			console.error('uploadOfflineChange failed:', error);
			finish();
		}
	});
};

const deleteOfflineChange = (dispatch, rowId, changesLength, callback) => {
	db.query('DELETE FROM offlineChanges WHERE rowid = ?', [rowId], () => {
		if (changesLength === 1) {
			dispatch(openAlert('Offline Changes Uploaded', "SiteWalker has completed uploading offline changes. If you're in a project, reload it to keep from overwriting changes."));
		}
		uploadOfflineChange(dispatch, callback);
	});
};

export const updateOfflineChangesState = () => {
	console.log('updateOfflineChangesState not yet implemented');
};

export const updateOfflineDatasetNote = (datasetId, noteData, updateNoteState = false) => {
	db.query('SELECT * FROM offlineDatasetsInfo WHERE Id = ?', [datasetId], dbResult => {
		try {
			if (!getRowsLength(dbResult)) return;
			const row = dbResult.rs.rows.item(0);
			const json = Utils.safeJSONParse(row?.JSON);
			let foundNote = false;

			if (!json) return;

			const activityIndex = json.activitiesMap?.[noteData.activityId];
			const activity = json.activities?.[activityIndex];
			if (!activity) return;
			const notes = Array.isArray(activity.notes) ? activity.notes : [];
			activity.notes = notes;
			if (!noteData.noteId) noteData.noteId = 'temp-' + (notes.length + 1);

			for (const i in notes) {
				if (notes[i].noteId === noteData.noteId) {
					notes[i] = noteData;
					foundNote = true;
					break;
				}
			}

			if (!foundNote) {
				notes.push(noteData);
			}

			activity.hasChanged = true;
			db.query('UPDATE offlineDatasetsInfo SET JSON = ? WHERE Id = ?', [JSON.stringify(json), datasetId]);
			if (typeof updateNoteState === 'function') updateNoteState(noteData);
		} catch (error) {
			console.error('updateOfflineDatasetNote failed:', error);
		}
	});
};

export const updateOfflineChangeNote = (datasetId, noteData) => {
	db.query('SELECT rowid, * FROM offlineChanges WHERE DatasetId = ? AND TaskId = ? AND Type = ?', [datasetId, noteData.activityId, 'setNote'], dbResult => {
		try {
			let rowId = false;
			const rowCount = getRowsLength(dbResult);

			for (let i = 0; i < rowCount; i++) {
				const row = dbResult.rs.rows.item(i);
				const json = Utils.safeJSONParse(row?.JSON);

				if (!json) continue;
				if (json.noteId === noteData.noteId) {
					rowId = row.rowid;
					break;
				}
			}

			let query = 'UPDATE offlineChanges SET JSON = ? WHERE rowid = ?';
			let params = [JSON.stringify(noteData), rowId];
			if (rowId === false) {
				query = 'INSERT INTO offlineChanges (DatasetId, TaskId, Type, JSON) VALUES (?, ?, ?, ?)';
				params = [datasetId, noteData.activityId, 'setNote', JSON.stringify(noteData)];
			}

			db.query(query, params);
		} catch (error) {
			console.error('updateOfflineChangeNote failed:', error);
		}
	});
};

export const updateOfflineProgress = (datasetId, progressData, isOnline, updateProgressState) => {
	db.query('SELECT * FROM offlineDatasetsInfo WHERE Id = ?', [datasetId], dbResult => {
		try {
			if (!getRowsLength(dbResult)) return;
			const row = dbResult.rs.rows.item(0);
			const json = Utils.safeJSONParse(row?.JSON);

			if (!json) return;

			const activityIndex = json.activitiesMap?.[progressData.activityId];
			const activity = json.activities?.[activityIndex];
			if (!activity) return;
			for (const i in progressData) {
				if (i in activity) activity[i] = progressData[i];
			}
			activity.hasChanged = true;
			db.query('UPDATE offlineDatasetsInfo SET JSON = ? WHERE Id = ?', [JSON.stringify(json), datasetId]);
		} catch (error) {
			console.error('updateOfflineProgress dataset sync failed:', error);
		}
	});

	if (!isOnline) {
		db.query('SELECT * FROM offlineChanges WHERE DatasetId = ? AND TaskId = ? AND Type = ?', [datasetId, progressData.activityId, 'setProgress'], dbResult => {
			try {
				const query = getRowsLength(dbResult)
					? 'UPDATE offlineChanges SET JSON = ? WHERE DatasetId = ? AND TaskId = ? AND Type = ?'
					: 'INSERT INTO offlineChanges (JSON, DatasetId, TaskId, Type) VALUES (?, ?, ?, ?)';
				const params = [JSON.stringify(progressData), datasetId, progressData.activityId, 'setProgress'];

				db.query(query, params, () => {
					if (typeof updateProgressState === 'function') {
						updateProgressState(progressData);
					}
				});
			} catch (error) {
				console.error('updateOfflineProgress queue failed:', error);
			}
		});
	}
};
